import { useRef, useState } from "react";
import {
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  Typography,
  Input,
  useTheme,
  Divider,
  Button,
  Link,
  Paper,
  Box,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import moment from "moment";
import MoedaText from "../../util/ui/inputs/moedaText";
import { useReactToPrint } from "react-to-print";
import { IconArrowLeft, IconPrinter } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { felixTokens, radius } from "../../../theme";

/** Célula do formulário. O DARF é um fac-símile de documento oficial: a grade
 *  precisa do traço cheio em tinta, não da hairline de tabela do app. */
const celulaDarf = {
  border: "1px solid",
  borderColor: "text.primary",
} as const;

/** Campo de dado do documento. O DARF é fac-símile de formulário da Receita:
 *  Verdana em TUDO, inclusive nos campos de valor — a IBM Plex Mono do sistema
 *  não entra aqui. `tabular-nums` fica, porque alinhar dígito é do formulário,
 *  não do design system. */
const inputDado = {
  fontFamily: "Verdana",
  fontVariantNumeric: "tabular-nums",
  fontSize: 14,
  color: "text.primary",
} as const;

export default function Darf() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state = {} } = location;
  const { mes, valorDarf, codigoDarf } = state;
  const theme = useTheme();
  const [cpf, setCpf] = useState<string>();
  const [nome, setNome] = useState<string>();
  const [observacao, setObservacao] = useState<string>();
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current ?? null,
  });

  return (
    <Container maxWidth="md" sx={{ px: 8, pt: 2, pb: 4 }}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box flexGrow={1} pr={2}>
          <Typography variant="eyebrow" color="text.secondary">
            DARF
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
            fontStyle="italic"
            color="text.secondary"
            pt={0.5}
          >
            * Após a data de vencimento, usar o{" "}
            <Link
              href="https://sicalc.receita.economia.gov.br/sicalc/rapido/contribuinte"
              sx={{ color: "accentInk", textDecorationColor: "currentColor" }}
            >
              Sicalc
            </Link>{" "}
            para gerar uma nova DARF acrescida de multa e juros.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
        <Box m={1 / 2} />
        <Button
          onClick={handlePrint}
          startIcon={<IconPrinter />}
          variant="contained"
          color="primary"
        >
          Imprimir
        </Button>
      </Box>
      {valorDarf > 0 || true ? (
        /* O DARF é fac-símile de formulário da Receita Federal: o design system
           NÃO se aplica dentro dele. Este tema local devolve a Verdana original
           e neutraliza as variantes mono/uppercase, para que rótulos oficiais
           como "09 VALOR DOS JUROS ... dl - 1.025/69" saiam exatamente como a
           Receita escreve — sem virar "DL - 1.025/69". */
        <ThemeProvider
          theme={createTheme({
            ...theme,
            typography: {
              ...theme.typography,
              fontFamily: "Verdana",
              labelMono: {
                ...theme.typography.labelMono,
                fontFamily: "Verdana",
                textTransform: "none",
                letterSpacing: 0,
              },
              eyebrow: {
                ...theme.typography.eyebrow,
                fontFamily: "Verdana",
                textTransform: "none",
                letterSpacing: 0,
              },
              dataMono: {
                ...theme.typography.dataMono,
                fontFamily: "Verdana",
                letterSpacing: 0,
              },
              caption: {
                ...theme.typography.caption,
                fontFamily: "Verdana",
                textTransform: "none",
                letterSpacing: 0,
              },
              overline: {
                ...theme.typography.overline,
                fontFamily: "Verdana",
                textTransform: "none",
                letterSpacing: 0,
              },
            },
          })}
        >
          <Container
            maxWidth="md"
            component={Paper}
            ref={componentRef}
            sx={{
              pt: 0,
              px: 8,
              pb: 4,
              borderRadius: `${radius.lg}px`,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
              // Impressão é sempre em papel branco: o documento força os tokens
              // do tema light, qualquer que seja o tema em uso no app.
              "@media print": {
                border: "none",
                backgroundColor: felixTokens.light.surface,
                color: felixTokens.light.ink,
                "& *": {
                  color: `${felixTokens.light.ink} !important`,
                  borderColor: `${felixTokens.light.ink} !important`,
                },
              },
            }}
          >
            <Typography
              variant="labelMono"
              color="text.secondary"
              sx={{
                display: "block",
                padding: theme.spacing(4, 0, 0),
                textAlign: "right",
              }}
            >
              1ª via
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={{ ...celulaDarf, width: "48%" }} rowSpan={3}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={3}>
                        <img
                          alt="img-brasao"
                          src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/Coat_of_arms_of_Brazil.svg/200px-Coat_of_arms_of_Brazil.svg.png"
                          style={{
                            maxHeight: "48px",
                            height: "48px",
                            margin: "auto",
                          }}
                        ></img>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          MINISTÉRIO DA FAZENDA
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 10,
                            fontWeight: 300,
                          }}
                        >
                          SECRETARIA DA RECEITA FEDERAL DO BRASIL
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            textAlign: "center",
                            fontSize: 9,
                            fontWeight: 300,
                          }}
                        >
                          Documento de Arrecadação de Receitas Federais
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.08em",
                          }}
                        >
                          DARF
                        </Typography>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ ...celulaDarf, width: "32%" }}>
                    <Typography variant="labelMono" color="text.secondary">
                      02 PERÍODO DE APURAÇÃO
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...celulaDarf, width: "20%" }}>
                    <Typography variant="dataMono">
                      {moment(mes).endOf("month").format("MM/YYYY")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      03 NÚMERO DO CPF / CNPJ
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Input
                      placeholder="Insira seu CPF"
                      disableUnderline
                      value={cpf}
                      onChange={(e) => setCpf(e.target.value)}
                      sx={inputDado}
                    />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      04 CÓDIGO DA RECEITA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="dataMono">{codigoDarf}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf} rowSpan={2}>
                    <Typography
                      variant="labelMono"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      01 NOME / RAZÃO SOCIAL
                    </Typography>
                    <Input
                      placeholder="Insira seu Nome Completo"
                      disableUnderline
                      fullWidth
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      sx={{ fontSize: 14, color: "text.primary" }}
                    />
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      05 NÚMERO DE REFERÊNCIA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}></TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      06 DATA DE VENCIMENTO
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="dataMono">
                      {moment(mes)
                        .add(1, "month")
                        .endOf("month")
                        .format("DD/MM/YYYY")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf} rowSpan={5}>
                    <Input
                      placeholder="Observações Opcionais"
                      disableUnderline
                      fullWidth
                      value={observacao}
                      onChange={(e) => setObservacao(e.target.value)}
                      sx={{ fontSize: 14, color: "text.primary" }}
                    />
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      07 VALOR DO PRINCIPAL
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={valorDarf} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      08 VALOR DA MULTA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={0} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      09 VALOR DOS JUROS E/OU ENCARGOS dl - 1.025/69
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={0} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      10 VALOR TOTAL
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    {/* Valor total: o número que o contribuinte confere. */}
                    <Typography variant="dataMono" sx={{ fontWeight: 600 }}>
                      <MoedaText value={valorDarf} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      11 AUTENTICAÇÃO
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {/* Linha de corte entre as duas vias. */}
            <Divider
              variant="fullWidth"
              sx={{ margin: theme.spacing(2, 0), borderStyle: "dashed" }}
            />
            <Typography
              variant="labelMono"
              color="text.secondary"
              sx={{ display: "block", textAlign: "right" }}
            >
              2ª via
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={{ ...celulaDarf, width: "48%" }} rowSpan={3}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={3}>
                        <img
                          alt="img-brasao"
                          src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/Coat_of_arms_of_Brazil.svg/200px-Coat_of_arms_of_Brazil.svg.png"
                          style={{
                            maxHeight: "48px",
                            height: "48px",
                            margin: "auto",
                          }}
                        ></img>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          MINISTÉRIO DA FAZENDA
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 10,
                            fontWeight: 300,
                          }}
                        >
                          SECRETARIA DA RECEITA FEDERAL DO BRASIL
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            textAlign: "center",
                            fontSize: 9,
                            fontWeight: 300,
                          }}
                        >
                          Documento de Arrecadação de Receitas Federais
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            textAlign: "center",
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.08em",
                          }}
                        >
                          DARF
                        </Typography>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ ...celulaDarf, width: "32%" }}>
                    <Typography variant="labelMono" color="text.secondary">
                      02 PERÍODO DE APURAÇÃO
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...celulaDarf, width: "20%" }}>
                    <Typography variant="dataMono">
                      {moment(mes).endOf("month").format("MM/YYYY")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      03 NÚMERO DO CPF / CNPJ
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Input
                      placeholder="Insira seu CPF"
                      disableUnderline
                      value={cpf}
                      onChange={(e) => setCpf(e.target.value)}
                      sx={inputDado}
                    />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      04 CÓDIGO DA RECEITA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="dataMono">{codigoDarf}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf} rowSpan={2}>
                    <Typography
                      variant="labelMono"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      01 NOME / RAZÃO SOCIAL
                    </Typography>
                    <Input
                      placeholder="Insira seu Nome Completo"
                      disableUnderline
                      fullWidth
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      sx={{ fontSize: 14, color: "text.primary" }}
                    />
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      05 NÚMERO DE REFERÊNCIA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}></TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      06 DATA DE VENCIMENTO
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="dataMono">
                      {moment(mes)
                        .add(1, "month")
                        .endOf("month")
                        .format("DD/MM/YYYY")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf} rowSpan={5}>
                    <Input
                      placeholder="Observações Opcionais"
                      disableUnderline
                      fullWidth
                      value={observacao}
                      onChange={(e) => setObservacao(e.target.value)}
                      sx={{ fontSize: 14, color: "text.primary" }}
                    />
                  </TableCell>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      07 VALOR DO PRINCIPAL
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={valorDarf} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      08 VALOR DA MULTA
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={0} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      09 VALOR DOS JUROS E/OU ENCARGOS dl - 1.025/69
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    <Typography variant="dataMono">
                      <MoedaText value={0} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      10 VALOR TOTAL
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf} align="right">
                    {/* Valor total: o número que o contribuinte confere. */}
                    <Typography variant="dataMono" sx={{ fontWeight: 600 }}>
                      <MoedaText value={valorDarf} />
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ height: 40 }}>
                  <TableCell sx={celulaDarf}>
                    <Typography variant="labelMono" color="text.secondary">
                      11 AUTENTICAÇÃO
                    </Typography>
                  </TableCell>
                  <TableCell sx={celulaDarf}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Container>
        </ThemeProvider>
      ) : (
        // Spinner é traço: `info` é o canal do accent-ink no tema.
        <CircularProgress variant="indeterminate" color="info" />
      )}
    </Container>
  );
}
